import { useCallback, useState } from 'react';
import type { ApiClient } from '../api';
import { BRAND_META, eventLabel, marketWords } from '../brand';
import { money, shortId, timeAgo } from '../format';
import type { FeedEvent } from '../types';
import { EventDetailSheet } from './EventDetail';

/**
 * The live event feed. Each row: brand pip, what happened, which entity,
 * amount when the role may see it, and how long ago. Entity ids are
 * opaque platform ids; the deep link on the row is the way to the full
 * record in the platform admin. Tapping a row opens everything the
 * dashboard holds about that event (EventDetail.tsx, 11 October 2026).
 */
export function Feed({
  events,
  onDeepLink,
  api,
}: {
  events: FeedEvent[] | null;
  onDeepLink?: (e: FeedEvent) => void;
  /** Lets a row open its full detail; without it rows are plain. */
  api?: ApiClient;
}) {
  const [open, setOpen] = useState<FeedEvent | null>(null);
  const close = useCallback(() => setOpen(null), []);
  if (events === null) return <div className="empty">Loading the event stream…</div>;
  if (events.length === 0) {
    return <div className="empty">No events yet in the last 7 days. The feed fills as the platforms emit.</div>;
  }
  return (
    <div className="feed">
      {events.map((e) => {
        const entityId = e.offer_id ?? e.creator_id ?? e.merchant_id ?? e.advertiser_id;
        // The learned name over a raw id; a shortened id until one flows in.
        // A sign-up row is the member; a purchase row is where it was bought,
        // with who bought it beside it (23 October 2026).
        const signup = e.event_type === 'member.joined';
        const entity = (signup ? e.member_name : null) ?? e.entity_name ?? e.stall_name ?? (entityId ? shortId(entityId) : null);
        const who = !signup && e.member_name ? e.member_name : null;
        const place = [e.city, e.country].filter(Boolean).join(', ');
        return (
          <div
            key={`${e.event_id}-${e.occurred_at}`}
            className={`feed-row${api ? ' clickable' : ''}`}
            {...(api
              ? {
                  role: 'button',
                  tabIndex: 0,
                  'aria-label': `${BRAND_META[e.brand].label} ${eventLabel(e.event_type)}, details`,
                  onClick: () => setOpen(e),
                  onKeyDown: (k: React.KeyboardEvent) => {
                    if (k.key === 'Enter' || k.key === ' ') {
                      k.preventDefault();
                      setOpen(e);
                    }
                  },
                }
              : {})}
          >
            <span className="pip" style={{ background: BRAND_META[e.brand].series }} aria-hidden />
            {/* The pip carries the brand hue; the label carries the same
                fact in words, so identity is never colour alone. */}
            <span className="brand-tag">{BRAND_META[e.brand].label}</span>
            <span className="what">
              {eventLabel(e.event_type)}
              {e.market ? ` · ${marketWords(e)}` : ''}
            </span>
            <span className="where">
              {[entity, who, place].filter(Boolean).join(' · ')}
            </span>
            {e.amount_minor !== null && e.currency && (
              <span className="amount">{money(e.amount_minor, e.currency)}</span>
            )}
            <span className="when">{timeAgo(e.occurred_at)}</span>
            {onDeepLink && entityId && (
              <button
                className="deeplink"
                onClick={(ev) => {
                  ev.stopPropagation(); // the arrow goes to the admin, not the detail
                  onDeepLink(e);
                }}
                title="Open in the platform admin"
              >
                →
              </button>
            )}
          </div>
        );
      })}
      {open && api && <EventDetailSheet api={api} event={open} onClose={close} onDeepLink={onDeepLink} />}
    </div>
  );
}
